import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client'
import type { InstrumentDetail } from '../api/types'

/** One ticked sensor's readings block: the last detail that arrived and,
 *  when the newest refresh failed, why. */
export interface SensorState {
  detail?: InstrumentDetail
  error?: string
}

type Settled = [number, PromiseSettledResult<InstrumentDetail>]

/** A refresh folded into what is on screen. A failed refresh keeps the last
 *  good readings and records the error beside them, the rule usePolled
 *  already follows: the block's own "received …" line says how old they are,
 *  which is the honest signal either way. Returns a new map. */
export function mergeRefresh(prev: Map<number, SensorState>,
                             results: Settled[]): Map<number, SensorState> {
  const next = new Map(prev)
  for (const [id, result] of results) {
    if (result.status === 'fulfilled') {
      next.set(id, { detail: result.value })
    } else {
      const reason = result.reason
      const error = reason instanceof Error ? reason.message : String(reason)
      next.set(id, { ...prev.get(id), error })
    }
  }
  return next
}

/** Only the ticked sensors. An unticked sensor's readings are dropped rather
 *  than cached, so ticking it again fetches it fresh instead of showing a
 *  block that stopped refreshing when it left the selection. */
export function keepSelected(prev: Map<number, SensorState>,
                             ids: number[]): Map<number, SensorState> {
  const wanted = new Set(ids)
  return new Map([...prev].filter(([id]) => wanted.has(id)))
}

/** The readings of every ticked sensor, each refreshed on the latest-readings
 *  panel's cadence.
 *
 *  Not usePolled, which clears everything whenever its question changes:
 *  ticking a fourth sensor would blank the three already on screen while it
 *  re-asked for all of them. Here a sensor is fetched when it is ticked,
 *  the others stay as they are, and one timer refreshes all of them.
 *  Changing station does clear, because the old answer belongs to a
 *  different station. Polling stops while the tab is hidden, as usePolled's
 *  does.
 *
 *  Each sensor's answer is merged the moment it lands, never held for the
 *  slowest of a batch, and only the newest request per sensor may land: a
 *  slow poll arriving after a quicker later one would otherwise put older
 *  readings back on screen with nothing to say so. */
export function useSensorDetails(code: string, ids: number[],
                                  intervalMs: number) {
  type Cache = { code: string; byId: Map<number, SensorState> }
  const [state, setState] = useState<Cache>(() => ({ code, byId: new Map() }))
  // What was last committed, readable synchronously: an answer landing
  // after the selection moved on is merged into the newest state, never
  // into the one current when it was asked.
  const cache = useRef<Cache>(state)
  const commit = (next: Cache) => {
    cache.current = next
    setState(next)
  }
  // Written in an effect, not during render: a render react-router starts
  // in a transition can be abandoned, and the timer must never act on a
  // selection that was not committed.
  const selected = useRef(ids)
  const latest = useRef(new Map<number, number>())
  const pending = useRef(new Set<number>())
  const idsKey = ids.join(',')

  const fetchInto = (wanted: number[]) => {
    for (const id of wanted) {
      const mine = (latest.current.get(id) ?? 0) + 1
      latest.current.set(id, mine)
      pending.current.add(id)
      api.instrument(code, id).then(
        (value): PromiseSettledResult<InstrumentDetail> => ({ status: 'fulfilled', value }),
        (reason: unknown): PromiseSettledResult<InstrumentDetail> => ({ status: 'rejected', reason }),
      ).then((result) => {
        if (latest.current.get(id) !== mine) return
        pending.current.delete(id)
        // A late answer for a station, or a sensor, the reader has left.
        if (cache.current.code !== code || !selected.current.includes(id)) return
        commit({ code, byId: mergeRefresh(cache.current.byId, [[id, result]]) })
      })
    }
  }

  // A newly ticked sensor is fetched at once; the rest keep their readings,
  // and one already on its way is not asked for twice.
  useEffect(() => {
    selected.current = ids
    const base = cache.current.code === code
      ? cache.current.byId : new Map<number, SensorState>()
    if (cache.current.code !== code) pending.current.clear()
    const byId = keepSelected(base, ids)
    commit({ code, byId })
    fetchInto(ids.filter((id) => !byId.has(id) && !pending.current.has(id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, idsKey])

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') fetchInto(selected.current)
    }
    const timer = window.setInterval(refresh, intervalMs)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refresh)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, intervalMs])

  return useMemo(() => {
    const byId = state.code === code ? state.byId : new Map<number, SensorState>()
    const details = ids.map((id) => byId.get(id)?.detail)
      .filter((d): d is InstrumentDetail => d !== undefined)
    return { byId, details, loading: ids.some((id) => !byId.has(id)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, code, idsKey])
}
